import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { IconButton, CircularProgress } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import UnifiedModal from './UnifiedModal';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';
import EmptyState from './EmptyState';
import Announcement from './Announcement';

const Announcements = () => {
  const { user } = useContext(Context);
  const { isAdvancedEditor } = useCategory();
  const { vertical_id: verticalId } = useParams();

  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [announcements, setAnnouncements] = useState(null);
  const [editItem, setEditItem] = useState(null);

  const fetchAnnouncements = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const url =
        process.env.REACT_APP_FLASK_WEBSERVER +
        'get_announcements' +
        (verticalId ? `?vertical_id=${encodeURIComponent(verticalId)}` : '');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      // Newest announcements first, so freshly added ones appear at the top.
      const sorted = [...(data.data || [])].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setAnnouncements(sorted);
    } catch (err) {
      console.error('Failed to fetch announcements', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [user?.uid, verticalId]);

  const handleSave = (updatedItem) => {
    setAnnouncements((prev) =>
      editItem
        ? prev.map((item) => (item._id === editItem._id ? updatedItem : item))
        : [updatedItem, ...prev]
    );
    setOpenModal(false);
    fetchAnnouncements();
  };

  const handleOpenModal = (item = null) => {
    setEditItem(item);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    setAnnouncements(announcements.filter((el) => el._id !== id));
    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}delete_announcement/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.log('Failed to delete announcement:', error);
    }
  };

  return (
    <div
      style={{
        padding: '12px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
          fontWeight: 600,
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}
      >
        <div style={{ display: 'flex', gap: '4px' }}>
          <img src="/announcement.svg" alt="" style={{ height: '24px', width: '24px' }} />
          ANNOUNCEMENTS
        </div>
        {isAdvancedEditor && (
          <div
            style={{
              color: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => handleOpenModal()}
          >
            <IconButton size="small" sx={{ color: '#1976d2' }}>
              <AddCircleIcon fontSize="small" />
            </IconButton>
            Add Announcement
          </div>
        )}
      </div>
      {loading ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <CircularProgress size={24} style={{ color: '#000' }} />
        </div>
      ) : announcements?.length > 0 ? (
        <div>
          {announcements.map((announcement) => (
            <Announcement
              id={announcement._id}
              title={announcement.title}
              details={announcement.details}
              deleteHandler={() => handleDelete(announcement._id)}
              editHandler={() => handleOpenModal(announcement)}
              assignedBy={announcement.assignedBy}
              createdAt={announcement.created_at}
              canEdit={isAdvancedEditor}
            />
          ))}
        </div>
      ) : isAdvancedEditor ? (
        <EmptyState
          imageSrc="/announcement.svg"
          type="announcement"
          onAddClick={() => handleOpenModal()}
        />
      ) : (
        <div
          style={{
            height: '100%',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <img
            src="/announcement.svg"
            alt="Empty State"
            style={{ height: '24px', width: '24px' }}
          />
          <p style={{ margin: 0, color: '#888' }}>No announcements found.</p>
        </div>
      )}

      <UnifiedModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        item={editItem}
        type="announcement"
        onSave={handleSave}
        isVertical={!!verticalId}
        verticalId={verticalId}
      />
    </div>
  );
};
export default Announcements;
